import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { plansAPI } from '../lib/api';
import { useFavourites } from '../context/FavouritesContext';
import { Funnel, MagnifyingGlass, ArrowRight, Heart } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { formatCostShort } from '../lib/format';
import { logger } from '../lib/logger';

const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'newest', label: 'Newest' },
  { value: 'area_desc', label: 'Largest area' },
  { value: 'popular', label: 'Most popular' }
];

const PlansPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768);
  const [sort, setSort] = useState('recommended');
  const { isFavourite, toggleFavourite } = useFavourites();

  const [filters, setFilters] = useState({
    bedrooms: [],
    floors: [],
    plan_type: [],
    min_price: '',
    max_price: ''
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const queryFilters = {};

      if (filters.bedrooms.length) queryFilters.bedrooms = filters.bedrooms.join(',');
      if (filters.floors.length) queryFilters.floors = filters.floors.join(',');
      if (filters.plan_type.length) queryFilters.plan_type = filters.plan_type.join(',');
      if (filters.min_price) queryFilters.min_price = filters.min_price;
      if (filters.max_price) queryFilters.max_price = filters.max_price;

      const response = await plansAPI.getPlans(queryFilters);
      setPlans(response.data);
    } catch (error) {
      logger.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (type, value) => {
    setFilters(prev => {
      if (Array.isArray(prev[type])) {
        const newArray = prev[type].includes(value)
          ? prev[type].filter(v => v !== value)
          : [...prev[type], value];
        return { ...prev, [type]: newArray };
      }
      return { ...prev, [type]: value };
    });
  };

  const applyFilters = () => fetchPlans();

  const resetFilters = async () => {
    // One-click reset: clear all filters AND fetch all plans immediately
    // (do not wait for React state to flush + re-derive - fetch with empty
    //  filter dict directly so the grid updates on the same click).
    setFilters({ bedrooms: [], floors: [], plan_type: [], min_price: '', max_price: '' });
    setSort('recommended');
    try {
      setLoading(true);
      const response = await plansAPI.getPlans({});
      setPlans(response.data);
    } catch (error) {
      logger.error('Failed to reset plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortedPlans = useMemo(() => {
    const arr = [...plans];
    switch (sort) {
      case 'price_asc':
        return arr.sort((a, b) => (a.base_price || 0) - (b.base_price || 0));
      case 'price_desc':
        return arr.sort((a, b) => (b.base_price || 0) - (a.base_price || 0));
      case 'newest':
        return arr.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      case 'area_desc':
        return arr.sort((a, b) => (b.area_sqm || 0) - (a.area_sqm || 0));
      case 'popular':
        return arr.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
      case 'recommended':
      default:
        return arr;
    }
  }, [plans, sort]);

  return (
    <div className="min-h-screen bg-background" data-testid="plans-page">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1
              className="text-4xl md:text-5xl font-black text-primary mb-2 tracking-tight"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Browse House Plans
            </h1>
            <p className="text-text_muted">Browse our collection of professionally designed house plans</p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label htmlFor="sort-select" className="text-sm text-text_muted hidden sm:inline">Sort</label>
              <select
                id="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="px-3 py-2 border border-border rounded text-sm bg-white focus:ring-2 focus:ring-accent"
                data-testid="sort-select"
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden p-2 border border-border rounded hover:bg-gray-50"
              data-testid="toggle-filters-btn"
              aria-label="Toggle filters"
            >
              <Funnel size={24} />
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside
            className={`${showFilters ? 'block' : 'hidden'} md:block w-full md:w-64 flex-shrink-0`}
            data-testid="filters-sidebar"
          >
            <div className="bg-white border border-border p-6 sticky top-20">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-primary" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Filters</h3>
                <button
                  onClick={resetFilters}
                  className="text-sm text-accent hover:underline"
                  data-testid="reset-filters-btn"
                >
                  Reset
                </button>
              </div>

              {/* Bedrooms */}
              <div className="mb-6">
                <h4 className="text-sm font-bold text-primary mb-3">Bedrooms</h4>
                <div className="space-y-2">
                  {[2, 3, 4, 5].map(num => (
                    <label key={num} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.bedrooms.includes(num)}
                        onChange={() => handleFilterChange('bedrooms', num)}
                        className="mr-2"
                        data-testid={`bedroom-filter-${num}`}
                      />
                      <span className="text-sm">{num} Bedrooms</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Floors */}
              <div className="mb-6">
                <h4 className="text-sm font-bold text-primary mb-3">Floors</h4>
                <div className="space-y-2">
                  {[1, 2].map(num => (
                    <label key={num} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.floors.includes(num)}
                        onChange={() => handleFilterChange('floors', num)}
                        className="mr-2"
                        data-testid={`floor-filter-${num}`}
                      />
                      <span className="text-sm">{num === 1 ? 'Single' : 'Double'} Storey</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Plan Type */}
              <div className="mb-6">
                <h4 className="text-sm font-bold text-primary mb-3">Plan Type</h4>
                <div className="space-y-2">
                  {['A', 'B', 'C', 'D'].map(type => (
                    <label key={type} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.plan_type.includes(type)}
                        onChange={() => handleFilterChange('plan_type', type)}
                        className="mr-2"
                        data-testid={`type-filter-${type}`}
                      />
                      <span className="text-sm">Type {type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h4 className="text-sm font-bold text-primary mb-3">Price Range</h4>
                <div className="space-y-3">
                  <input
                    type="number"
                    placeholder="Min price"
                    value={filters.min_price}
                    onChange={(e) => handleFilterChange('min_price', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded text-sm"
                    data-testid="min-price-input"
                  />
                  <input
                    type="number"
                    placeholder="Max price"
                    value={filters.max_price}
                    onChange={(e) => handleFilterChange('max_price', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded text-sm"
                    data-testid="max-price-input"
                  />
                </div>
              </div>

              <button
                onClick={applyFilters}
                className="w-full bg-primary text-white py-3 rounded font-medium hover:bg-opacity-90 transition-colors"
                data-testid="apply-filters-btn"
              >
                Apply Filters
              </button>
            </div>
          </aside>

          {/* Plans Grid */}
          <main className="flex-1">
            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="mt-4 text-text_muted">Loading plans...</p>
              </div>
            ) : sortedPlans.length === 0 ? (
              <div className="text-center py-20 bg-white border border-border">
                <MagnifyingGlass size={64} className="text-text_muted mx-auto mb-4" />
                <p className="text-text_muted">No plans found matching your criteria</p>
                <button
                  onClick={resetFilters}
                  className="mt-4 text-accent hover:underline"
                  data-testid="reset-filters-empty-btn"
                >
                  Reset filters and try again
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedPlans.map((plan, index) => (
                  <motion.div
                    key={plan.plan_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Link
                      to={`/plans/${plan.reference_number || plan.plan_id}`}
                      className="block bg-white border border-border hover:-translate-y-1 transition-transform group"
                      data-testid={`plan-card-${plan.plan_id}`}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                          src={plan.image_url}
                          alt={plan.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavourite(plan.plan_id); }}
                          className={`absolute top-3 right-3 p-2 rounded-full transition-colors ${isFavourite(plan.plan_id) ? 'bg-red-500 text-white' : 'bg-white/95 text-gray-700 hover:bg-white'}`}
                          data-testid={`favourite-card-${plan.plan_id}`}
                          aria-label="Toggle favourite"
                        >
                          <Heart size={18} weight={isFavourite(plan.plan_id) ? 'fill' : 'regular'} />
                        </button>
                      </div>
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-lg font-bold text-primary line-clamp-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                            {plan.name}
                          </h3>
                          <span
                            className="text-xs font-mono bg-gray-100 px-2 py-1 rounded ml-2 flex-shrink-0"
                            style={{ fontFamily: 'JetBrains Mono, monospace' }}
                          >
                            {plan.plan_type}
                          </span>
                        </div>
                        {plan.reference_number && (
                          <div className="text-[10px] font-mono text-text_muted mb-2 uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            Ref: {plan.reference_number}
                          </div>
                        )}
                        <p className="text-sm text-text_muted mb-4 line-clamp-2">{plan.description}</p>

                        <div className="grid grid-cols-3 gap-1.5 mb-3 text-[11px]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          <div className="border border-border p-2 text-center">
                            <div className="text-text_muted text-[9px] uppercase">Area</div>
                            <div className="font-bold text-primary">{plan.area_sqm}m²</div>
                          </div>
                          <div className="border border-border p-2 text-center">
                            <div className="text-text_muted text-[9px] uppercase">Beds</div>
                            <div className="font-bold text-primary">{plan.bedrooms}</div>
                          </div>
                          <div className="border border-border p-2 text-center">
                            <div className="text-text_muted text-[9px] uppercase">Baths</div>
                            <div className="font-bold text-primary">{plan.bathrooms || 1}</div>
                          </div>
                          <div className="border border-border p-2 text-center">
                            <div className="text-text_muted text-[9px] uppercase">Floors</div>
                            <div className="font-bold text-primary">{plan.floors}</div>
                          </div>
                          <div className="border border-border p-2 text-center">
                            <div className="text-text_muted text-[9px] uppercase">Est. Cost</div>
                            <div className="font-bold text-primary">{formatCostShort(plan.estimated_construction_cost)}</div>
                          </div>
                          <div className="border border-border p-2 text-center">
                            <div className="text-text_muted text-[9px] uppercase">Stand</div>
                            <div className="font-bold text-primary">{plan.min_stand_size_sqm > 0 ? `${plan.min_stand_size_sqm}m²` : '—'}</div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs text-text_muted mb-1">Starting from</div>
                            <div className="text-2xl font-bold text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              R{plan.base_price.toFixed(2)}
                            </div>
                          </div>
                          <div className="flex items-center text-accent text-sm font-medium group-hover:translate-x-2 transition-transform">
                            <span>View</span>
                            <ArrowRight size={16} weight="bold" className="ml-1" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default PlansPage;
